import { SidePanelFilter } from '@getsynapse/design-system';
import { useContext, useEffect, useMemo, useState } from 'react';
import GeneralDashboardFilters from './GeneralDashboardFilters';
import { useDispatch, useSelector } from 'react-redux';
import { DashboardContext } from 'Pages/DashboardsDetailsPage/DashboardContext';
import {
  applyLocalFilters,
  dashboardFiltersSelectors,
  restoreDashboardFilters,
  updateDashboardFilters,
} from 'state/normalized/dashboard/dashboardFiltersSlice';
import { dashboardSelectors } from 'state/normalized/dashboard/dashboardSlice';
import { RootState } from 'state/store';
import { DashboardFilters } from 'utils/types/dashboardFilters';
import intl from 'react-intl-universal';

type DashboardFiltersPanelProps = {
  isOpen: boolean;
  onClose: () => void;
};

const DashboardFiltersPanel = ({
  isOpen,
  onClose,
}: DashboardFiltersPanelProps) => {
  const dispatch = useDispatch();
  const { dashboardId } = useContext(DashboardContext);
  const { canFilter } = useSelector((state: RootState) =>
    dashboardSelectors.selectDashboardAffordanceFlags(state, dashboardId)
  );
  const appliedFilters = useSelector((state: RootState) =>
    dashboardFiltersSelectors.selectAppliedFiltersById(state, dashboardId)
  );
  const [localFilters, setLocalFilters] = useState<DashboardFilters>({});
  const [canApplyFilters, setCanApplyFilters] = useState<boolean>(false);

  useEffect(() => {
    setLocalFilters(appliedFilters);
  }, [appliedFilters]);

  const appliedFiltersCount = useMemo<number>(
    () => Object.keys(appliedFilters).length,
    [appliedFilters]
  );

  const handleClearAllFilters = () => {
    if (!canFilter) {
      dispatch(restoreDashboardFilters({ dashboardId }));
      return;
    }
    dispatch(updateDashboardFilters({ dashboardId, filters: {} }));
    setCanApplyFilters(false);
  };

  const handleApplyFilters = () => {
    if (canFilter) {
      dispatch(
        updateDashboardFilters({
          dashboardId,
          filters: localFilters,
        })
      );
    } else {
      dispatch(applyLocalFilters({ dashboardId, filters: localFilters }));
    }
    setCanApplyFilters(false);
  };

  const changeFilter = (filters: DashboardFilters) => {
    setLocalFilters(filters);
    setCanApplyFilters(true);
  };

  return (
    <SidePanelFilter
      open={isOpen}
      onClose={onClose}
      onClearAllFilters={handleClearAllFilters}
      onApplyFilters={handleApplyFilters}
      canApplyFilters={canApplyFilters}
      appliedFiltersCount={appliedFiltersCount}
      clearAllButtonLabel={
        !canFilter
          ? intl.get('FILTER_PANEL.RESET_FILTERS')
          : intl.get('FILTER_PANEL.CLEAR_ALL')
      }
    >
      <div className='flex flex-col space-y-5 mt-6 mb-6'>
        <GeneralDashboardFilters
          filters={localFilters}
          onFilterChange={changeFilter}
          canFilter={canFilter}
        />
      </div>
    </SidePanelFilter>
  );
};

export default DashboardFiltersPanel;
